<script setup lang="ts">
// Insiders' company name, licence and RERA ORN with copy buttons, so an agent closing a deal
// can paste exactly what the developer needs to register the booking under Insiders.
// `menu` sits inside the avatar dropdown; `card` stands alone on the profile page.
import { INSIDERS_COMPANY } from '~/constants/insidersCompany';

const { variant = 'card' } = defineProps<{
  variant?: 'menu' | 'card';
}>();

type FieldKey = 'legalName' | 'licenceNo' | 'reraOrn' | 'all';

const COPIED_RESET_MS = 1500;

const { t } = useI18n();
const uiStore = useUIStore();
const copiedKey = ref<FieldKey | null>(null);

const fields = computed(() => [
  { key: 'legalName' as const, label: t('nav.insidersLicence.companyName'), value: INSIDERS_COMPANY.legalName },
  { key: 'licenceNo' as const, label: t('nav.insidersLicence.licenceNo'), value: INSIDERS_COMPANY.licenceNo },
  { key: 'reraOrn' as const, label: t('nav.insidersLicence.reraOrn'), value: INSIDERS_COMPANY.reraOrn },
]);

const allDetails = computed(() => fields.value.map((field) => `${field.label}: ${field.value}`).join('\n'));

const copy = async (key: FieldKey, value: string) => {
  try {
    await navigator.clipboard.writeText(value);
    copiedKey.value = key;
    setTimeout(() => {
      if (copiedKey.value === key) copiedKey.value = null;
    }, COPIED_RESET_MS);
  } catch {
    uiStore.showSnackBanner('error', t('nav.insidersLicence.copyFailed'));
  }
};

const copyLabel = (key: FieldKey) =>
  copiedKey.value === key ? t('nav.insidersLicence.copied') : t('nav.insidersLicence.copy');
</script>

<template>
  <section
    :class="
      variant === 'menu'
        ? 'flex flex-col gap-2 border-b border-black/[0.08] bg-black-03 px-4 py-3'
        : 'flex flex-col gap-3 rounded-2xl border border-black/[0.08] bg-white p-5'
    "
    :aria-label="t('nav.insidersLicence.title')"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-0.5">
        <p
          class="font-mono uppercase tracking-[0.12em] text-black-60"
          :class="variant === 'menu' ? 'text-[10px]' : 'text-[11px]'"
        >
          {{ t('nav.insidersLicence.title') }}
        </p>
        <p
          v-if="variant === 'card'"
          class="text-[13px] text-black-60"
        >
          {{ t('nav.insidersLicence.hint') }}
        </p>
      </div>
      <button
        type="button"
        class="shrink-0 rounded-full border border-black/[0.12] px-2.5 py-1 text-[11px] font-semibold text-black-80 transition-colors hover:bg-white"
        @click="copy('all', allDetails)"
      >
        {{ copiedKey === 'all' ? t('nav.insidersLicence.copied') : t('nav.insidersLicence.copyAll') }}
      </button>
    </div>
    <dl class="flex flex-col gap-1.5">
      <div
        v-for="field in fields"
        :key="field.key"
        class="flex items-center justify-between gap-3"
      >
        <div class="min-w-0">
          <dt class="text-[11px] text-black-60">{{ field.label }}</dt>
          <dd
            class="break-words font-semibold text-black-100"
            :class="variant === 'menu' ? 'text-[12px]' : 'text-[14px]'"
          >
            {{ field.value }}
          </dd>
        </div>
        <button
          type="button"
          class="shrink-0 text-[11px] font-semibold text-black-80 underline decoration-gold-primary-120 decoration-2 underline-offset-4 transition-colors hover:text-black-100"
          :aria-label="`${copyLabel(field.key)} ${field.label}`"
          @click="copy(field.key, field.value)"
        >
          {{ copyLabel(field.key) }}
        </button>
      </div>
    </dl>
  </section>
</template>
